// src/lib/trackedApplicationsService.ts
//
// Service de sync cloud pour le tracker de candidatures (PWA C3).
//
// Stratégie :
// - Tant que l'user n'est pas connecté : 100% LocalStorage (C1)
// - Quand l'user se connecte via Magic Link :
//   1. Upload du LocalStorage actuel vers Supabase (preserve les IDs)
//   2. Download de la version cloud (au cas où plusieurs devices)
//   3. Merge (last-write-wins basé sur lastUpdate)
//   4. Re-écriture du LocalStorage avec le résultat fusionné
// - Toute modification ultérieure : sync immédiat (upsert) vers Supabase
//
// Conflict resolution : si même id en local et cloud, on garde la version
// avec le lastUpdate le plus récent.

import { supabase } from './supabase';

// ─── Types (alignés sur Application dans suivi-candidature.tsx) ─────────

export type Status =
  | 'wishlist'
  | 'applied'
  | 'phone'
  | 'interview'
  | 'offer'
  | 'rejected'
  | 'accepted';

export interface Application {
  id: string;
  company: string;
  role: string;
  url?: string;
  status: Status;
  appliedAt: string; // ISO
  lastUpdate: string; // ISO
  notes?: string;
  salary?: string;
  source?: string;
  contact?: string;
  nextAction?: string;
  nextActionAt?: string; // ISO
}

// ─── DB row shape (snake_case → camelCase) ───────────────────────────────

interface AppRow {
  id: string;
  user_id: string;
  company: string;
  role: string;
  url: string | null;
  status: Status;
  applied_at: string;
  last_update: string;
  notes: string | null;
  salary: string | null;
  source: string | null;
  contact: string | null;
  next_action: string | null;
  next_action_at: string | null;
  created_at: string;
  updated_at: string;
}

function rowToApp(r: AppRow): Application {
  return {
    id: r.id,
    company: r.company,
    role: r.role,
    url: r.url ?? undefined,
    status: r.status,
    appliedAt: r.applied_at,
    lastUpdate: r.last_update,
    notes: r.notes ?? undefined,
    salary: r.salary ?? undefined,
    source: r.source ?? undefined,
    contact: r.contact ?? undefined,
    nextAction: r.next_action ?? undefined,
    nextActionAt: r.next_action_at ?? undefined,
  };
}

function appToRow(app: Application, userId: string): Partial<AppRow> {
  return {
    id: app.id,
    user_id: userId,
    company: app.company,
    role: app.role,
    url: app.url ?? null,
    status: app.status,
    applied_at: app.appliedAt,
    last_update: app.lastUpdate,
    notes: app.notes ?? null,
    salary: app.salary ?? null,
    source: app.source ?? null,
    contact: app.contact ?? null,
    next_action: app.nextAction ?? null,
    next_action_at: app.nextActionAt ?? null,
  };
}

// ─── Magic Link auth ─────────────────────────────────────────────────────

/**
 * Envoie un Magic Link à l'email donné. L'utilisateur clique sur le lien
 * dans son email → revient sur le site connecté → la sync s'active.
 *
 * Bonus stratégique : l'email est enregistré dans auth.users → lead capture
 * pour pousser des offres /offres ciblées plus tard.
 */
export async function sendMagicLink(email: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // Redirect après click du Magic Link
        emailRedirectTo: `${window.location.origin}/ressources/outils/suivi-candidature`,
        // Crée l'user s'il n'existe pas (mode sign-up auto)
        shouldCreateUser: true,
        data: {
          source: 'suivi-candidatures-tracker',
          consented_at: new Date().toISOString(),
        },
      },
    });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Erreur inconnue' };
  }
}

// ─── Sync UP : push LocalStorage → cloud ─────────────────────────────────

/**
 * Upsert toutes les apps données vers Supabase pour l'user courant.
 * Les apps avec un id qui existe déjà sont mises à jour.
 * Les apps sans id existant sont insérées.
 */
export async function pushApplications(
  apps: Application[],
  userId: string,
): Promise<{ ok: boolean; synced: number; error?: string }> {
  if (apps.length === 0) return { ok: true, synced: 0 };
  const rows = apps.map((a) => appToRow(a, userId));
  const { error, data } = await supabase
    .from('tracked_applications')
    .upsert(rows, { onConflict: 'id' })
    .select('id');

  if (error) return { ok: false, synced: 0, error: error.message };
  return { ok: true, synced: data?.length ?? 0 };
}

// ─── Sync DOWN : pull cloud → local ──────────────────────────────────────

export async function pullApplications(
  userId: string,
): Promise<{ ok: boolean; apps: Application[]; error?: string }> {
  const { data, error } = await supabase
    .from('tracked_applications')
    .select('*')
    .eq('user_id', userId)
    .order('last_update', { ascending: false });

  if (error) return { ok: false, apps: [], error: error.message };
  const apps = (data as AppRow[] | null)?.map(rowToApp) ?? [];
  return { ok: true, apps };
}

// ─── Merge (conflict resolution) ─────────────────────────────────────────

/**
 * Merge local et cloud avec stratégie last-write-wins.
 * Pour chaque id, on garde la version dont lastUpdate est le plus récent.
 */
export function mergeApplications(
  local: Application[],
  cloud: Application[],
): Application[] {
  const merged = new Map<string, Application>();

  for (const app of cloud) {
    merged.set(app.id, app);
  }

  for (const app of local) {
    const existing = merged.get(app.id);
    if (!existing) {
      merged.set(app.id, app);
    } else {
      // Last-write-wins
      const localTime = new Date(app.lastUpdate).getTime();
      const cloudTime = new Date(existing.lastUpdate).getTime();
      if (localTime > cloudTime) {
        merged.set(app.id, app);
      }
    }
  }

  return Array.from(merged.values()).sort(
    (a, b) => new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime(),
  );
}

// ─── Sync complet : appelé au login (Magic Link confirmé) ────────────────

export async function performFullSync(
  localApps: Application[],
  userId: string,
): Promise<{ ok: boolean; merged: Application[]; error?: string }> {
  // 1. Pull cloud
  const pulled = await pullApplications(userId);
  if (!pulled.ok) return { ok: false, merged: localApps, error: pulled.error };

  // 2. Merge
  const merged = mergeApplications(localApps, pulled.apps);

  // 3. Push back la version mergée (pour s'assurer que cloud = local après sync)
  const pushed = await pushApplications(merged, userId);
  if (!pushed.ok) return { ok: false, merged, error: pushed.error };

  return { ok: true, merged };
}

// ─── Operations atomiques (utilisées en CRUD quotidien après login) ──────

export async function upsertApplication(
  app: Application,
  userId: string,
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase
    .from('tracked_applications')
    .upsert(appToRow(app, userId), { onConflict: 'id' });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export async function deleteApplication(
  id: string,
  userId: string,
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase
    .from('tracked_applications')
    .delete()
    .eq('id', id)
    .eq('user_id', userId);
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

// ─── Sign out ────────────────────────────────────────────────────────────

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}
